// openEventStream — the SSE transport (contract 03 §6a) and the ONLY `event-source-polyfill`
// import in the app. v3 had these ~50 lines copy-pasted at three call sites
// (`useSSENotifications.js`, `useOnboardingLiveWatch.js`, `views/Panel/AdminChartTypes.vue`),
// each re-deriving the same header, the same 45s heartbeat and the same parse guard.
//
// The polyfill (rather than native `EventSource`) exists for exactly one reason: `EventSource`
// cannot set an `Authorization` header and `/events/stream/` is a protected endpoint. The token
// arrives through the injected `getToken()` — like the rest of this module, the stream never
// imports auth (ledger A1).
//
// Transport ONLY. The debounce queues, polling fallbacks, visibility handling and
// workspace-scoped resource syncs wrapped around the three v3 sites stay with their owners;
// this file opens a stream, parses frames and hands them over. Lifecycle is likewise the
// CONSUMER's — it calls `close()` on unmount / workspace switch / tab hidden, exactly as v3
// does. `shared/http` exposes no composable (§9); the Vue-facing wrapper lives one layer up.
//
// Scope note: §6a also declares `backend?: 'app' | 'rag'`. There is no RAG client to point at
// (ledger B5 keeps traffic on appHttp — see instances.ts) and `/events/stream/` is a Django app
// route at all three v3 sites, so the option is intentionally absent. Adding it is additive.
import { EventSourcePolyfill } from 'event-source-polyfill'

import { ApiError } from './ApiError'
import { getHttpDeps } from './configure'

import { useEnv } from '@/shared/config'

/** v3's magic number, repeated verbatim at all three call sites. */
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 45_000

export interface EventStreamOptions {
  /** Path on the app API, e.g. `/events/stream/`. */
  path: string
  /** Query appended to `path` (URL-encoded here), e.g. `{ workspace_id }`. */
  query?: Record<string, string | number>
  /** Event name → handler. The frame body is `JSON.parse`d before the handler sees it. */
  events: Record<string, (data: unknown) => void>
  onOpen?: () => void
  /**
   * Connection failure. Narrowed from §6a's `unknown` to `ApiError` so that the one case the
   * consumer must branch on — `Unauthorized` (§14) — is a typed `kind`, not a status sniff on a
   * raw polyfill event. The stream is deliberately still open when this fires: the consumer
   * decides teardown, and the polyfill is meanwhile reconnecting on its own.
   */
  onError?: (error: ApiError) => void
  /** Heartbeat timeout in ms; default 45_000 (the v3 value). */
  heartbeatTimeoutMs?: number
}

export interface EventStreamHandle {
  /** Tear the stream down. The consumer owns this — nothing in here closes the stream. */
  close: () => void
  readonly connected: boolean
}

export function openEventStream(options: EventStreamOptions): EventStreamHandle {
  const token = getHttpDeps()?.getToken() ?? null
  const source = new EventSourcePolyfill(buildUrl(options.path, options.query), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    heartbeatTimeout: options.heartbeatTimeoutMs ?? DEFAULT_HEARTBEAT_TIMEOUT_MS,
    withCredentials: false,
  })

  let connected = false
  let closed = false

  for (const [name, handler] of Object.entries(options.events)) {
    source.addEventListener(name, (event) => {
      let data: unknown
      try {
        data = JSON.parse(frameBody(event))
      } catch {
        return // malformed (or empty) frame — drop it and leave the stream open, v3 parity
      }
      handler(data)
    })
  }

  // `closed` replaces v3's `if (es !== source) return` guard: a callback that arrives after
  // teardown must not resurrect `connected` or reach the consumer.
  source.onopen = (): void => {
    if (closed) return
    connected = true
    options.onOpen?.()
  }

  source.onerror = (event): void => {
    if (closed) return
    connected = false
    // NEVER close() here — the polyfill auto-reconnects, and tearing it down would turn a
    // transient hiccup (or a 401 the consumer may still recover from) into a dead stream.
    options.onError?.(streamError(event))
  }

  return {
    close: (): void => {
      closed = true
      connected = false
      source.close()
    },
    get connected(): boolean {
      return connected
    },
  }
}

function buildUrl(path: string, query: EventStreamOptions['query']): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) params.set(key, String(value))
  const search = params.toString()
  return `${useEnv().API_APP_URL}${path}${search ? `?${search}` : ''}`
}

/** A custom-named listener is typed as a bare `Event` by the polyfill; the body is on `.data`. */
function frameBody(event: unknown): string {
  return (event as { data?: string }).data ?? ''
}

/**
 * The polyfill dispatches two error shapes, both through `onerror`: a `ConnectionEvent` carrying
 * `status`/`statusText` when the response is not 200 (401 → `unauthorized`), and an `ErrorEvent`
 * carrying `error` for a transport failure or a heartbeat timeout (no status).
 */
function streamError(event: unknown): ApiError {
  const { status, statusText, error } = event as {
    status?: number
    statusText?: string
    error?: unknown
  }
  if (typeof status === 'number' && status > 0) {
    return ApiError.fromStatus(status, statusText || `Event stream response status ${status}`)
  }
  const detail = error instanceof Error ? error.message : 'Event stream connection failed'
  return ApiError.fromStatus(null, detail)
}
